import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

// Api
import getUserApi from '../auth/api/getUserInfo.api.auth';

// Events
import unauthorizedEvt from '../auth/events/unauthorized.event.auth';

// Lib
import authToken from '../auth/lib/token.lib.auth';

// Storage
import tokenStorage from '../auth/storage/token.storage.auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({
    initialized: false,
    token: null,
    user: null,
    isAdmin: false,
  });

  const checkToken = useCallback(async (token) => {
    try {
      const user = await getUserApi({ token });
      setState({
        initialized: true,
        token,
        user,
        isAdmin: user?.isAdmin || false,
      });
      tokenStorage.add(token);
      authToken.set(token);
    } catch (error) {
      setState({
        initialized: true,
        token: null,
        user: null,
        isAdmin: false,
      });
      tokenStorage.remove();
      authToken.remove();
    }
  }, []);

  const init = useCallback(() => {
    const token = tokenStorage.get();
    if (!token) {
      setState({
        initialized: true,
        token: null,
        user: null,
        isAdmin: false,
      });
      return;
    }
    checkToken(token);
  }, [checkToken]);

  const signIn = useCallback(({ token, user }) => {
    try {
      tokenStorage.add(token);
      authToken.set(token);
      setState({
        initialized: true,
        token,
        user,
        isAdmin: user?.isAdmin || false,
      });
    } catch (error) {
      setState({
        initialized: true,
        token: null,
        user: null,
        isAdmin: false,
      });
      tokenStorage.remove();
      authToken.remove();
    }
  }, []);

  const signOut = useCallback(() => {
    tokenStorage.remove();
    authToken.remove();
    setState({
      initialized: true,
      token: null,
      user: null,
      isAdmin: false,
    });
  }, []);

  useEffect(() => {
    // Subscribe to unauthorized event
    const unsubscribe = unauthorizedEvt.subscribe(() => {
      signOut();
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [signOut]);

  const value = {
    ...state,
    init,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

